/**
 * Where a person stands on their OWN subscription plan (engines spec 2026-09-28 §6.8).
 *
 * Read by the box from the engine's own stream (Claude Code: every `rate_limit_event`), sent up
 * as ENGINE_STANDING and remembered in `engine_accounts`, so Settings, the doors and the
 * pre-flight can answer without waking a sleeping computer. Vestra never reads a vendor token
 * or calls a vendor usage endpoint to get it.
 *
 * Display rules belong to the reader: every reading shows its age, a window past its reset
 * shows a dash, a missing number is never shown as 0, and 80% and up is gold.
 *
 * Zod-free on purpose (a browser bundle may import it); the frame's schema lives in
 * schemas/event-payloads.schema.ts and reads the lists below.
 */
import type { PlanHeadsUpReading } from '../schemas/plan-heads-up.schema.js'; // a type only: still zod-free

/** Engines that run on the person's own plan. Keep in lockstep with engine_accounts_engine_check. */
export const SUBSCRIPTION_ENGINES = ['claude_code', 'codex'] as const; // grok_build joins in T4
export type SubscriptionEngine = (typeof SUBSCRIPTION_ENGINES)[number];
/** A plan engine: the one name every package uses for an engine that runs on the person's own plan. */
export type PlanEngine = (typeof SUBSCRIPTION_ENGINES)[number];

export const STANDING_WINDOW_KINDS = ['five_hour', 'weekly', 'weekly_model', 'overage'] as const;
export type StandingWindowKind = (typeof STANDING_WINDOW_KINDS)[number];

export interface StandingWindow {
  kind: StandingWindowKind;
  /** e.g. the vendor's per-model bucket for weekly_model. */
  label?: string;
  /** 0..100; null = not reported (never shown as 0). */
  usedPct: number | null;
  /** ISO 8601; null = not reported. */
  resetsAt: string | null;
}

export const STANDING_STATUSES = ['ok', 'warning', 'exhausted', 'unknown'] as const;
export type StandingStatus = (typeof STANDING_STATUSES)[number];

export interface EngineStanding {
  engine: SubscriptionEngine;
  /** e.g. 'max', 'pro' (from the engine's auth status), null if unknown. */
  tier: string | null;
  windows: StandingWindow[];
  status: StandingStatus;
  source: 'stream' | 'pull';
  /** ISO 8601. */
  measuredAt: string;
  /** The account's slot this reading is for (several accounts, spec 2026-10-01). Absent = 'primary'. */
  slotId?: string;
}

export const STANDING_WARNING_PCT = 80;

export function isSubscriptionEngine(v: unknown): v is SubscriptionEngine {
  return typeof v === 'string' && (SUBSCRIPTION_ENGINES as readonly string[]).includes(v);
}

/** The vendor's name for the plan each subscription engine runs on ("your Claude plan"). */
export const ENGINE_PLAN_NAME: Record<SubscriptionEngine, string> = { claude_code: 'Claude', codex: 'ChatGPT' };
/** The engine's own name as the product shows it. 'native' is Vestra. */
export const ENGINE_LABEL: Record<'native' | SubscriptionEngine, string> = { native: 'Vestra', claude_code: 'Claude Code', codex: 'Codex' };
/** The company whose terms a plan engine runs under. For the terms line only, never a label. */
export const ENGINE_VENDOR: Record<SubscriptionEngine, string> = { claude_code: 'Anthropic', codex: 'OpenAI' };

/**
 * How a person signs in to a plan: the vendor's page in the box's browser, or a code they enter on
 * the vendor's page from any device (engines T3). An absent method on the wire means 'browser'.
 */
export const ENGINE_SIGN_IN_METHOD_IDS = ['browser', 'device_code'] as const;
export type EngineSignInMethod = (typeof ENGINE_SIGN_IN_METHOD_IDS)[number];
/** The ways each plan engine can sign in. The first is the one a Connect starts with. */
export const ENGINE_SIGN_IN_METHODS: Record<SubscriptionEngine, readonly EngineSignInMethod[]> = {
  claude_code: ['browser'],
  codex: ['browser', 'device_code'],
};

/**
 * Engines T5: "Your Claude plan is used up", the one line every surface WITHOUT the plan_limit
 * card leads with (the needs-you card, a chat, the attention chip, the Platform API summary).
 * Takes the engine id as stored on the request; an id this build does not know is never shown.
 */
export function planUsedUpLine(engine: string): string {
  return isSubscriptionEngine(engine) ? `Your ${ENGINE_PLAN_NAME[engine]} plan is used up` : 'Your plan is used up';
}

/**
 * The reading a plan heads-up is said from, derived from its schema (schemas/plan-heads-up.schema.ts).
 * A type-only import: this module stays zod-free.
 */
export type { PlanHeadsUpReading } from '../schemas/plan-heads-up.schema.js';

/** "the next 2 hours": rounded the way a person says it, never "0 minutes". */
function untilPhrase(ms: number): string {
  const minutes = Math.max(1, Math.round(ms / 60_000));
  if (minutes < 60) return minutes === 1 ? 'the next minute' : `the next ${minutes} minutes`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return hours === 1 ? 'the next hour' : `the next ${hours} hours`;
  return `the next ${Math.round(hours / 24)} days`;
}

/**
 * The plan heads-up, word for word from the approved mock (2026-09-28, chapter 5): "Your Claude
 * plan is 84% used for the next 2 hours." Several accounts (spec 2026-10-01): `account` names the
 * account the turn ran on in place of the plan, "keshav@vestra.ai is 84% used for the next 2 hours."
 * The box says the first; the relay says the second from the same reading.
 */
export function planHeadsUpLine(engine: SubscriptionEngine, r: PlanHeadsUpReading, account?: string | null): string {
  const lead = `${account || `Your ${ENGINE_PLAN_NAME[engine]} plan`} is ${r.usedPct}% used`;
  const left = r.resetsAt ? Date.parse(r.resetsAt) - Date.parse(r.measuredAt) : NaN;
  return Number.isFinite(left) && left > 0 ? `${lead} for ${untilPhrase(left)}.` : `${lead}.`;
}
